import React, { useState, useEffect, useRef } from "react";
import type { ApplicationFormData, FormStep } from "../types/application";
import { motion, AnimatePresence } from "framer-motion";
import ResumeUploadStep from "../components/application/ResumeUploadStep";
import ContactInfoEmploymentStep from "../components/application/ContactInfoEmploymentStep";
import PrioritiesEntitlementsStep from "../components/application/PrioritiesEntitlementsStep";
import EmploymentStatusStep from "../components/application/EmploymentStatusStep";
import LanguageStep from "../components/application/LanguageStep";
import SecurityEducationStep from "../components/application/SecurityEducationStep";
import ITCompetenciesStep from "../components/application/ITCompetenciesStep";
import ReviewStep from "../components/application/ReviewStep";
import SuccessStep from "../components/application/SuccessStep";
import ConsentStep from "../components/application/ConsentStep";
import { isMobile } from "react-device-detect";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import AIConsentStep from "../components/application/AIConsentStep";
import Header from "../components/application/Header";
import Footer from "../components/application/Footer";
import DateModified from "../components/common/dateModified";

const ApplyPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const activeStepRef = useRef(currentStep);
  const lastNavigationSourceStepRef = useRef<number | null>(null);
  const [submissionResult, setSubmissionResult] = useState<{
    applicationId?: string;
  }>({});
  const [formData, setFormData] = useState<ApplicationFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    linkedInUrl: "",
    AIConsentStatus: "",
    currentTitle: "",
    currentCompany: "",
    yearsOfExperience: 0,
    technicalSkills: [],
    softSkills: [],
    certifications: [],
    experiences: [],
    education: [],
    coverLetter: "",
    portfolioUrl: "",
    availabilityDate: "",
    salaryExpectation: "",
    workLocation: "onsite",
    positionTitle: "",
    enhancedData: {},
    documentId: "",
    extractionMetadata: {
      wasExtracted: false,
      extractedFields: [],
      missingFields: [],
    },
  });

  const { language } = useLanguage();
  const t = translations[language];

  // ...rest of your state and logic...
  // Set document title
  useEffect(() => {
    document.title = t.applyPageTitle;
  }, [t.applyPageTitle]);

  const steps: FormStep[] = [
    {
      id: "consent-page",
      title: t.consentHeader,
      description: t.consentSubHeader,
      component: ConsentStep,
    },
    {
      id: "ai-consent",
      title: t.statusAIConsent,
      description: t.aiConsentSubHeader,
      component: AIConsentStep,
    },
    {
      id: "resume-upload",
      title: t.statusResumeUploaded,
      description: t.resumeUploadDesc,
      component: ResumeUploadStep,
    },
    {
      id: "contact-employment",
      title: t.statusContactInformation,
      description: t.contactInfoSubheader,
      component: ContactInfoEmploymentStep,
    },
    {
      id: "priorities-entitlements",
      title: t.statusPriorities,
      description: t.prioritiesSubheader,
      component: PrioritiesEntitlementsStep,
    },
    {
      id: "employment-status",
      title: t.statusEmployment,
      description: t.employmentSubheader,
      component: EmploymentStatusStep,
    },
    {
      id: "language",
      title: t.statusLanguageInformation,
      description: t.languageSectionTitle,
      component: LanguageStep,
    },
    {
      id: "security-education",
      title: t.statusSecurityEducation || "Security & Education",
      description: t.securityEducationSectionTitle,
      component: SecurityEducationStep,
    },
    {
      id: "it-competencies",
      title: t.statusSkillsCompetencies,
      description: t.itCompetenciesSubheader,
      component: ITCompetenciesStep,
    },
    {
      id: "review",
      title: t.statusReview,
      description: t.applicationSummarySubheader,
      component: ReviewStep,
    },
    {
      id: "success",
      title: t.applicationSubmittedHeader,
      description: t.applicationsubmittedtitle,
      component: SuccessStep,
    },
  ];

  const updateFormData = (data: Partial<ApplicationFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  activeStepRef.current = currentStep;

  const handleNext = () => {
    const sourceStep = currentStep;
    if (
      sourceStep !== activeStepRef.current ||
      lastNavigationSourceStepRef.current === sourceStep ||
      sourceStep >= steps.length - 1
    ) return;

    lastNavigationSourceStepRef.current = sourceStep;
    setCurrentStep((prev) =>
      prev === sourceStep ? Math.min(prev + 1, steps.length - 1) : prev
    );
  };

  const handlePrevious = () => {
    const sourceStep = currentStep;
    if (
      sourceStep !== activeStepRef.current ||
      lastNavigationSourceStepRef.current === sourceStep ||
      sourceStep <= 0
    ) return;

    lastNavigationSourceStepRef.current = sourceStep;
    setCurrentStep((prev) =>
      prev === sourceStep ? Math.max(prev - 1, 0) : prev
    );
  };

  const navigateToStep = (stepIndex: number) => {
    const sourceStep = currentStep;
    if (
      sourceStep === activeStepRef.current &&
      lastNavigationSourceStepRef.current !== sourceStep &&
      stepIndex !== currentStep &&
      stepIndex >= 0 &&
      stepIndex < steps.length - 1
    ) {
      // Don't allow navigation to success step
      lastNavigationSourceStepRef.current = sourceStep;
      setCurrentStep(stepIndex);
    }
  };

  // Auto-scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const currentStepData = steps[currentStep];
  const StepComponent = currentStepData.component;
  // Render desktop mode
  if (!isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50">
        {/* Skip Link for Accessibility */}
        <a href="#main-content" className="skip-link">
          {t.skipToMainContent}
        </a>
        {/* Header */}
        <Header />
        {/* Page Title Section */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <h1 id="main-content" tabIndex={-1} className="text-4xl font-bold text-gray-900 mb-2">
              {t.defenceTeamCareers}
            </h1>
            <div className="w-24 h-1 bg-red-600"></div>
          </div>
        </div>

        {/* Status Bar - Stepper */}
        {currentStep > 0 && currentStep < steps.length - 2 && (
          <div className="py-8">
            <div className="max-w-7xl mx-auto px-4">
              <nav aria-labelledby="application-steps-heading">
              <h2 className="wb-inv" id="application-steps-heading">
                {t.applicationSteps}
              </h2>
              <div className="flex flex-wrap items-start justify-between gap-y-4">
                {steps.slice(1, -1).map((step, index) => {
                  const actualIndex = index + 1; // Adjust for skipping first step
                  return (
                    <React.Fragment key={step.id}>
                      <div className="flex flex-col items-center flex-1 min-w-0">
                        {/* Circle */}
                        <div
                          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mb-2 ${
                            actualIndex === currentStep
                              ? "bg-[#26374a] border-[#26374a]"
                              : actualIndex < currentStep
                                ? "bg-[#26374a] border-[#26374a]"
                                : "bg-white border-gray-300"
                          }`}
                        >
                          {actualIndex < currentStep && (
                            <svg
                              className="w-4 h-4 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {actualIndex === currentStep && (
                            <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                          )}
                        </div>
                        {/* Label */}
                        <span
                          className={`text-sm text-center break-words max-w-32 ${
                            actualIndex === currentStep
                              ? "text-gray-900 font-semibold"
                              : "text-gray-700"
                          }`}
                        >
                          {step.title}
                        </span>
                      </div>
                      {/* Connecting Line */}
                      {index < steps.length - 3 && (
                        <div
                          className={`flex-1 h-0.5 mt-[15px] ${
                            actualIndex < currentStep
                              ? "bg-[#26374a]"
                              : "bg-gray-300"
                          }`}
                        ></div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              </nav>
            </div>
          </div>
        )}
        {/* Main Content */}

        <main className="max-w-7xl mx-auto px-4 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="min-h-[60vh]"
            >
              {/* Step Header */}
              <div className="text-left mb-4">
                <motion.h2
                  className="text-3xl font-bold text-gray-900 mb-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  {currentStepData.title}
                </motion.h2>
              </div>

              {/* Step Content */}
              <div className="">
                <StepComponent
                  data={formData}
                  onUpdate={updateFormData}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  isFirst={currentStep === 0}
                  isLast={currentStep === steps.length - 1}
                  submissionResult={submissionResult}
                  setSubmissionResult={setSubmissionResult}
                  navigateToStep={navigateToStep}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Date Modified */}
        <DateModified />

        {/* GCWeb Footer */}
        <Footer />
      </div>
    );
  } else
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50">
        <a href="#main-content" className="skip-link">
          {t.skipToMainContent}
        </a>
        {/* Header */}
        <Header />

        {/* Page Title Section */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <h1 id="main-content" tabIndex={-1} className="text-2xl font-bold text-gray-900 mb-2">
              {t.defenceTeamCareers}
            </h1>
            <div className="w-16 h-1 bg-red-600"></div>
          </div>
        </div>

        {/* GCWeb Progress Bar */}
        {currentStep > 0 && currentStep < steps.length - 2 && (
          <div className="max-w-7xl mx-auto px-4 py-6">
            <section className="gc-prog" aria-labelledby="mobile-progress-heading">
              <h2 className="wb-inv" id="mobile-progress-heading">
                {t.applicationSteps}
              </h2>

              {/* Progress text */}
              <p>
                <strong>{t.yourProgress}:</strong>{" "}
                {Math.round((currentStep / (steps.length - 2)) * 100)}%
              </p>

              {/* Progress bar */}
              <div className="progress mrgn-bttm-0">
                <div className="progress">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    aria-valuenow={Math.round((currentStep / (steps.length - 2)) * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{
                      width: `${(currentStep / (steps.length - 2)) * 100}%`,
                    }}
                  >
                    {Math.round((currentStep / (steps.length - 2)) * 100)}%
                </div>
              </div>
              </div>

              {/* Step label */}
              <p>
                <strong>{t.currentStep}:</strong> {currentStepData.title}
              </p>
            </section>
          </div>
        )}

        {/* Step Content */}
        <main className="bg-white rounded-xl p-4 w-full">
          <StepComponent
            data={formData}
            onUpdate={updateFormData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isFirst={currentStep === 0}
            isLast={currentStep === steps.length - 1}
            submissionResult={submissionResult}
            setSubmissionResult={setSubmissionResult}
            navigateToStep={navigateToStep}
          />
        </main>
        {/* Date Modified */}
        <DateModified />
        {/* GCWeb Footer */}
        <Footer />
      </div>
    );
};

export default ApplyPage;
